import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { toastr } from "react-redux-toastr";
import Avatar from "components/Avatar";
import Loading from "components/Loading";
import { timesService } from "services";
import { formatLocalizedDate } from "utils/dateConfig";
import {
	getTimeRegistrationLogRange,
	getTimeRegistrationLogType,
} from "./timeRegistrationLog";

const formatLocalTime = (iso) => {
	if (!iso) return "--:--";
	const m = moment.utc(iso).local();
	if (!m.isValid()) return "--:--";
	return m.format("HH:mm");
};

const getDeviceLabel = (log) => {
	return log.start_origin || log.end_origin || "-";
};

const TimeRegistrationLogModal = ({ time, handleClose }) => {
	const { t, i18n } = useTranslation();

	const renderValueRange = (fromIso, toIso) => {
		return (
			<div>
				<span className="color-primary">{formatLocalTime(fromIso)}</span>{" "}
				{t("times.logModal.to")}{" "}
				<span className="color-primary">{formatLocalTime(toIso)}</span>
			</div>
		);
	};

	const getValueLabel = (log) => {
		const range = getTimeRegistrationLogRange(log);
		if (!range) return "-";
		return renderValueRange(range.from, range.to);
	};
	const [loading, setLoading] = useState(true);
	const [logs, setLogs] = useState([]);

	useEffect(() => {
		let active = true;

		const run = async () => {
			if (!time?.id || time.id.toString().includes("temp_")) {
				setLoading(false);
				setLogs([]);
				return;
			}

			setLoading(true);
			try {
				const response = await timesService.getLog(time.id);
				if (!active) return;

				if (response && response.ok === false) {
					toastr.error(
						"Error",
						response.errors?.[0]?.description ||
						t('times.logModal.errorLoading')
					);
					setLogs([]);
					return;
				}

				if (Array.isArray(response)) {
					setLogs(response);
					return;
				}

				setLogs(Array.isArray(response?.data) ? response.data : []);
			} finally {
				if (active) setLoading(false);
			}
		};

		run();

		return () => {
			active = false;
		};
	}, [time?.id]);

	const dayLabel = useMemo(() => {
		const base = time?.start || time?.day;
		if (!base) return "";
		const m = moment.utc(base).local();
		if (!m.isValid()) return "";
		return formatLocalizedDate(m, "long");
	}, [time?.start, time?.day, i18n.language]);

	if (loading) {
		return (
			<div style={{ padding: 20, minWidth: 720 }}>
				<Loading />
			</div>
		);
	}

	return (
		<div
			className="time-registration-log-modal"
			style={{ padding: 20, minWidth: 720 }}
		>
			<div
				style={{
					marginBottom: 10,
					marginLeft: 10,
					fontWeight: 600,
					fontSize: 20,
				}}
			>
				{dayLabel}
			</div>
			<table className="table time-registration-log-table">
				<thead>
					<tr>
						<th>{t('times.logModal.modifiedBy')}</th>
						<th>{t('times.logModal.value')}</th>
						<th>{t('times.logModal.modifiedDate')}</th>
						<th>{t('times.logModal.ip')}</th>
						<th>{t('times.logModal.device')}</th>
						<th>{t('times.logModal.type')}</th>
					</tr>
				</thead>
				<tbody>
					{logs.length === 0 ? (
						<tr>
							<td colSpan={6}>{t('times.logModal.noLogs')}</td>
						</tr>
					) : (
						logs.map((log) => {
							const employee = log.employee;
							const name = employee
								? `${employee.name || ""} ${employee.surname || ""}`.trim()
								: "-";

							const type = getTimeRegistrationLogType(log);
							return (
								<tr key={log.id}>
									<td>
										<div
											style={{ display: "flex", alignItems: "center", gap: 8 }}
										>
											{employee?.id ? (
												<Avatar
													userId={employee.id}
													className=""
													style={{ width: 28, height: 28, borderRadius: 999 }}
												/>
											) : null}
											<span className="color-primary">{name}</span>
										</div>
									</td>
									<td>{getValueLabel(log)}</td>
									<td>
										{log.created
											? moment.utc(log.created).local().format("DD/MM/YYYY HH:mm:ss")
											: "-"}
									</td>
									<td>{log.ip || "-"}</td>
									<td>{getDeviceLabel(log)}</td>
									<td>
										<span
											className={`badge ${type === "Entrada" ? "bg-success" : "bg-warning"}`}
										>
											{type === "Entrada" ? t('times.logModal.entry') : type === "Salida" ? t('times.logModal.exit') : type}
										</span>
									</td>
								</tr>
							);
						})
					)}
				</tbody>
			</table>

			<div style={{ display: "flex", justifyContent: "flex-end" }}>
				<button
					type="button"
					className="btn btn-outline-primary"
					onClick={handleClose}
				>
					{t('common.close')}
				</button>
			</div>
		</div>
	);
};

export default TimeRegistrationLogModal;
